import { displaySoftHyphens } from "#/_packages/umbrella/utils/soft-hyphens";
import { cn } from "tailwind-variants";
import { PageTags } from "#/_packages/umbrella/features/page-tags/page-tags";
import type {
	ApiBlockGridItemModel,
	ApiBlockItemModel,
	RatingUmbBlockPartialElementModel,
	StandardUmbPageContentResponseModel,
} from "#/_generated/umbraco/delivery-api/models";
import { RatingUmbBlockPartialElementModelContentType } from "#/_generated/umbraco/delivery-api/models";
import { Breadcrumbs } from "#/_packages/umbrella/features/breadcrumbs/components/breadcrumbs.tsx";
import { PageSurvey } from "#/_packages/umbrella/features/page-survey/components/page-survey.tsx";
import { useGetSiteSettings } from "#/_packages/umbrella/hooks/useGetSiteSettings.ts";
import { BlockGrid } from "#/_packages/umbrella/integrations/umbraco/property-components/block-grid.tsx";
import type { PageContent } from "#/_packages/umbrella/types";

export default function StandardUmbPage({
	content,
	breadcrumbs,
}: PageContent<StandardUmbPageContentResponseModel>) {
	const siteSettings = useGetSiteSettings();
	const properties = content.properties;
	const pageTitle = displaySoftHyphens(
		properties?.heading || content.name || "",
	);
	const mainContentContainer = toMainContentContainerClass(
		siteSettings?.mainContentContainer,
	);
	const mainContentContainerAlignment = toMainContentContainerAlignmentClass(
		siteSettings?.mainContentContainerAlignment,
	);

	return (
		<>
			{!properties?.hideBreadcrumb ? (
				<Breadcrumbs
					ancestors={breadcrumbs?.items ? (breadcrumbs.items ?? []) : []}
					currentPage={pageTitle}
					mainContentContainer={toMainContentContainer(
						siteSettings?.mainContentContainer,
					)}
					mainContentContainerAlignment={toMainContentContainerAlignment(
						siteSettings?.mainContentContainerAlignment,
					)}
				/>
			) : null}

			{!properties?.hidePageTitle && pageTitle ? (
				<div className="page-title-wrapper">
					<div
						className={cn(mainContentContainer, mainContentContainerAlignment)}
					>
						<h1 className="page-title">{pageTitle}</h1>
					</div>
				</div>
			) : null}

			<div className={properties?.noVerticalSpacing ? "no-row-gap" : undefined}>
				<BlockGrid
					prioritizeLeadingMedia
					noVerticalSpacing={content.properties?.noVerticalSpacing ?? false}
					className="_layout _block-grid"
					contentArea={properties?.blockGrid}
				/>

				<PageTags content={content} />
				{!properties?.hidePageSurvey && siteSettings?.enablePageSurvey ? (
					<PageSurvey
						umbracoContentId={content.id}
						voteQuestion={siteSettings.voteQuestion ?? undefined}
						votePositiveLabel={siteSettings.votePositiveLabel ?? undefined}
						voteNegativeLabel={siteSettings.voteNegativeLabel ?? undefined}
						heading={siteSettings.heading ?? undefined}
						introHtml={siteSettings.intro?.markup}
						questions={toPageSurveyQuestions(siteSettings.questions?.items)}
						enableComment={siteSettings.enableComment ?? true}
						commentLabel={siteSettings.commentLabel ?? undefined}
						commentHelpTextHtml={siteSettings.commentHelpText?.markup}
						submitBtnLabel={siteSettings.submitBtnLabel ?? undefined}
						errorMessage={siteSettings.errorMessage ?? undefined}
						confirmationMessageHtml={siteSettings.confirmationMessage?.markup}
					/>
				) : null}
			</div>
		</>
	);
}

function toPageSurveyQuestions(
	items?: (ApiBlockItemModel | ApiBlockGridItemModel)[],
) {
	return (items ?? []).filter(isRatingBlock).flatMap((item) => {
		const label = item.content.properties?.ratingLabel?.trim();
		return label ? [{ id: item.content.id, label }] : [];
	});
}

function isRatingBlock(
	item: ApiBlockItemModel | ApiBlockGridItemModel,
): item is ApiBlockItemModel & { content: RatingUmbBlockPartialElementModel } {
	return (
		"content" in item &&
		item.content.contentType ===
			RatingUmbBlockPartialElementModelContentType.ratingUmbBlockPartial
	);
}

function toMainContentContainer(value?: string | null) {
	switch (value) {
		case "fluidToSM":
		case "fluidToMD":
		case "fluidToLG":
		case "fluidToXL":
		case "fluidToXXL":
		case "alwaysFluid":
			return value;
		default:
			return "alwaysContained";
	}
}

function toMainContentContainerAlignment(value?: string | null) {
	switch (value) {
		case "start":
		case "end":
			return value;
		default:
			return "center";
	}
}

function toMainContentContainerClass(value?: string | null) {
	switch (value) {
		case "fluidToSM":
			return "container-sm";
		case "fluidToMD":
			return "container-md";
		case "fluidToLG":
			return "container-lg";
		case "fluidToXL":
			return "container-xl";
		case "fluidToXXL":
			return "container-xxl";
		case "alwaysFluid":
			return "container-fluid";
		default:
			return "container ";
	}
}

function toMainContentContainerAlignmentClass(value?: string | null) {
	switch (value) {
		case "start":
			return "container-start";
		case "end":
			return "container-end";
		default:
			return "";
	}
}
